
import { 
  NovelActSchema, 
  NovelActTree, 
  NovelChapterSchema, 
  NovelSceneSchema, 
  NovelSchema,
  CodexCategorySchema,
  CodexFieldSchema,
  CodexEntrySchema,
  CoreEntityType
 } from "#/index";
import { CodexDetail, ExecutionContext, NovelLabel, OutlineTree, PointOfView } from "../types";

const filterScene = (trees: NovelActTree[], checkFunction: (scene: NovelSceneSchema) => boolean|undefined): NovelActTree[] => {
  return trees.filter(act => {
    act.children = act.children?.filter(chapter => {
      chapter.children = chapter.children?.filter(scene => scene.data.type === CoreEntityType.NovelScenes && checkFunction(scene.data));
      return chapter.children?.length;
    });
    return act.children?.length;
  });
}

class NovelScene {
  context: ExecutionContext;
  result: NovelSceneSchema[] = [];
  private mode: 'union' | 'intersection' = 'union';
  private initialized: boolean = false;

  constructor(context: ExecutionContext, mode: 'union' | 'intersection' = 'union') {
    this.context = context;
    this.mode = mode;
  }

  useUnion(reset: boolean = true): this {
    this.mode = 'union';
    if (reset) {
      this.result = [];
      this.initialized = false;
    }
    return this;
  }

  useIntersection(reset: boolean = true): this {
    this.mode = 'intersection';
    if (reset) {
      this.result = [];
      this.initialized = false;
    }
    return this;
  }

  private applyFilter(items: Array<NovelSceneSchema | undefined>): void {
    const scenes = items.filter(Boolean) as NovelSceneSchema[];

    if (this.mode === 'union') {
      const existedIds = new Set(this.result.map(scene => scene.id));
      scenes.forEach(scene => {
        if (!existedIds.has(scene.id)) {
          existedIds.add(scene.id);
          this.result.push(scene);
        }
      });
      return;
    }

    // 交集模式
    if (!this.initialized) {
      this.result = scenes.slice();
      this.initialized = true;
      return;
    }

    const sceneIds = new Set(scenes.map(scene => scene.id));
    this.result = this.result.filter(scene => sceneIds.has(scene.id));
  }

  private getSceneById(id: string): NovelSceneSchema | undefined {
    return this.context!.globalContext.generateStore.getState().novelScenes.find(d => d.id === id);
  }

  getResult() {
    return this.result;
  }
  getOutline() {
    const ids = this.result.map(d => d.id);
    const novelActsTree = this.context!.globalContext.generateStore.getState().novelActsTree;
    return filterScene(novelActsTree, (scene: NovelSceneSchema) => ids.includes(scene.id));
  }
  initResult(data: NovelSceneSchema[]): this {
    this.result = data || this.context!.globalContext.generateStore.getState().novelScenes;
    this.initialized = this.mode === 'intersection';
    return this;
  }
  byActsTree(outline: NovelActTree[]): this {
    const scenes: NovelSceneSchema[] = [];
    outline.forEach(act => {
      act.children?.forEach(chapter => {
        chapter.children?.forEach(scene => {
          if (scene.data.type === CoreEntityType.NovelScenes) {
            scenes.push(scene.data as NovelSceneSchema);
          }
        });
      });
    });
    this.applyFilter(scenes);
    return this;
  }
  byNovel(novel: NovelSchema): NovelScene {
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    const scenes = novelScenes.filter(scene => scene.relationships.project.type === 'novels' && scene.relationships.project.id === novel.id);
    this.applyFilter(scenes);
    return this;
  }
  byAct(act: NovelActSchema): NovelScene {
    const { novelActsTree } = this.context!.globalContext.generateStore.getState();
    const actTree = novelActsTree.find(d => d.id === act.id);
    const scenes: NovelSceneSchema[] = [];
    actTree?.children?.forEach(chapter => {
      chapter.children?.forEach(scene => {
        if (scene.data.type === CoreEntityType.NovelScenes) {
          scenes.push(scene.data as NovelSceneSchema);
        }
      });
    });
    this.applyFilter(scenes);
    return this;
  }
  byChapter(chapter: NovelChapterSchema): NovelScene {
    const scenes = chapter.relationships.scenes?.map(id => this.getSceneById(id)) || [];
    this.applyFilter(scenes);
    return this;
  }
  byScene(scene: NovelSceneSchema): NovelScene {
    this.applyFilter([scene]);
    return this;
  }
 
  byPov(pov: PointOfView): NovelScene {
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    const scenes = novelScenes.filter(scene => {
      return scene.attributes.pov?.type === pov.value
        && (scene.attributes.pov?.characterId ? scene.attributes.pov?.characterId === pov.characterId : true);
    });
    this.applyFilter(scenes);
    return this;
  }
  byLabel(label: NovelLabel): NovelScene {
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    const scenes = novelScenes.filter(scene => scene.relationships.labels.includes(label.data.id));
    this.applyFilter(scenes);
    return this;
  }
  byCodexEntry(entry: CodexEntrySchema): NovelScene {
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    const scenes = novelScenes.filter(scene => scene.attributes.pov?.characterId === entry.id || scene.relationships.references.includes(entry.id));
    this.applyFilter(scenes);
    return this;
  }
  byCodexCategory(category: CodexCategorySchema): NovelScene {
    const { codexEntryGroups, novelScenes } = this.context!.globalContext.generateStore.getState()
    const group = codexEntryGroups.find(group => group.category?.id === category.id)
    const scenes = novelScenes.filter(scene => {
      return group?.children.some(entry => {
        return (scene.attributes.pov?.characterId === entry.id 
          || scene.relationships.references.includes(entry.id))
      })
    }) || [];
    this.applyFilter(scenes);
    return this;
  }
  byCodexDetail(detail: CodexDetail): NovelScene {
    const { novelScenes } = this.context!.globalContext.generateStore.getState()
    const scenes = novelScenes.filter(scene => {
      return (scene.attributes.pov?.characterId === detail.entry.id || scene.relationships.references.includes(detail.entry.id)) 
    });
    this.applyFilter(scenes);
    return this;
  }
  byText(text: string): this {
    const { novelScenes } = this.context!.globalContext.generateStore.getState();
    const scenes = novelScenes.filter(scene => scene.attributes.title === text);
    this.applyFilter(scenes);
    return this;
  }
}

export const NovelSceneUtils = NovelScene;